import { legacy, toast, wrapGlobal } from '../runtime/legacy.js';
import { WEB_ORIGIN } from '../config/env.js';

/**
 * Password reset, end to end.
 *
 * `resetPasswordForEmail` was already wired to the "Forgot password?" link, so
 * the mail went out — and that is where the journey stopped. The link pointed
 * at `…/#/reset-password`, a route the app did not have, behind a screen that
 * did not exist. Nobody could finish a reset.
 *
 * That fragment is also why no session ever arrived. GoTrue hands back an
 * implicit-grant link by *concatenating* its own fragment onto the redirect
 * target (`redirectURL + "#" + params`), so a target that already carried one
 * came back doubled:
 *
 *     https://app/#/reset-password#access_token=…&type=recovery
 *
 * To the browser that is a single fragment, so supabase-js read the parameter
 * name as `/reset-password#access_token`, found no `access_token`, and
 * established nothing.
 *
 * The redirect target here therefore carries no fragment at all. It marks
 * itself with a query parameter — `?recovery=1` — which GoTrue leaves intact
 * and which survives supabase-js clearing the fragment out from under us.
 */

const RESET_ROUTE = '/reset-password';

/** Matches the `minlength` the signup form asks for, and GoTrue's own floor. */
const MIN_PASSWORD_LENGTH = 6;

/**
 * Where the reset mail should send someone back to.
 *
 * Deliberately fragment-free — see the note above. Exported so auth-forms.js
 * and this module cannot drift apart: the link that goes out and the screen
 * that receives it have to agree.
 *
 * @param {Location | URL} location
 * @param {string} webOrigin Public origin, for builds that have no usable one.
 */
export function resetRedirectTarget(location, webOrigin = WEB_ORIGIN) {
  const isHttp = location.protocol === 'http:' || location.protocol === 'https:';
  // Capacitor serves the bundle from `capacitor://localhost` (iOS) or
  // `https://localhost` (Android). No mail client can open either, so the store
  // builds point at the web origin and let the deep link hand control back.
  // A dev server is also on localhost but always carries a port, which is what
  // separates the two.
  const isNativeShell = !isHttp || (location.hostname === 'localhost' && !location.port);
  const origin = (isNativeShell ? webOrigin : location.origin).replace(/\/$/, '');
  return `${origin}/?recovery=1`;
}

/**
 * Reads a recovery callback out of the current URL.
 *
 * Handles three shapes:
 *  - the target above, `?recovery=1#access_token=…&type=recovery`;
 *  - the doubled fragment from links sent by earlier builds, which are sitting
 *    in inboxes now and should still work;
 *  - GoTrue's failure redirect, `#error=access_denied&error_code=otp_expired…`.
 *
 * @returns {{accessToken: string, refreshToken: string, error: string,
 *            errorDescription: string} | null} Null when this is an ordinary
 *   route rather than a callback.
 */
export function parseRecoveryCallback({ search = '', hash = '' } = {}) {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  // Everything after the *last* `#` is GoTrue's half, whether or not the
  // redirect target contributed one of its own.
  const params = new URLSearchParams(fragment.slice(fragment.lastIndexOf('#') + 1));
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  const flagged = query.get('recovery') === '1';
  const isRecovery = params.get('type') === 'recovery';
  if (!flagged && !isRecovery) return null;

  return {
    accessToken: params.get('access_token') || '',
    refreshToken: params.get('refresh_token') || '',
    error: params.get('error_code') || params.get('error') || '',
    errorDescription: params.get('error_description') || '',
  };
}

/** Remembered at install time — the fragment does not survive the first route. */
let callback = null;

/**
 * True from the moment a recovery callback is seen until the reset is finished
 * or abandoned.
 *
 * The URL alone is not a durable record that a reset is in progress. This
 * module strips the tokens out of it on the first route, deliberately — and
 * anything else that rewrites the URL afterwards can then erase the reset route
 * with nothing left to rebuild it from, which drops the user on whatever `/`
 * renders. That is exactly what supabase-js used to do here: `detectSessionInUrl`
 * consumed the link, signed the user in, and cleared the fragment with
 * `location.hash = ''` a few hundred milliseconds later. It is off now (see the
 * note in public/legacy/core.js), but the screen should not depend on no one
 * else ever touching the URL, so the flag — not the address bar — is what says
 * a reset is underway.
 */
let recoveryActive = false;

function capture() {
  if (!callback) callback = parseRecoveryCallback(window.location);
  return callback;
}

/** Ends the reset, so ordinary routing resumes. */
function leaveRecovery() {
  recoveryActive = false;
  callback = null;
}

/**
 * Points the URL at `route` without navigating to it.
 *
 * `replaceState` rather than assigning `location.hash`, because assigning fires
 * a `hashchange` and the legacy router answers it immediately — which on the
 * way out would bounce `#/dashboard` straight back to `#/`, the session not yet
 * being one `initApp` has seen. This leaves the address bar correct and lets
 * the reload be the thing that routes.
 */
function replaceRoute(route) {
  const { origin, pathname } = window.location;
  window.history.replaceState(null, '', `${origin}${pathname}#${route}`);
}

/**
 * Hides the nav. The legacy router does this itself for its own signed-out
 * routes, and this route never reaches it — someone part-way through a reset
 * has nowhere to navigate to. `handleRoute` shows both again on the next route,
 * so nothing has to undo it here.
 *
 * The mobile header is left alone deliberately: its display is decided by
 * `!important` media queries at every width, so login and signup show it too.
 */
function hideChrome() {
  ['sidebar', 'bottom-nav'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.classList.add('hidden');
      el.style.display = 'none';
    }
  });
}

/**
 * `error_description` is read straight out of the URL fragment, so anyone can
 * put anything in it by handing someone a crafted link. It is rendered because
 * GoTrue's own wording ("Email link is invalid or has expired") is better than
 * a guess — but it is rendered as text, never as markup.
 */
function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
}

function shell(inner) {
  return `
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-logo">
          <span class="logo-icon">⬡</span>
          <span class="logo-text font-display auth-logo-text">Iron<span class="accent">Pact</span></span>
        </div>
        <div class="card auth-form-card">${inner}</div>
      </div>
    </div>
  `;
}

function deadLinkView(title, message) {
  return shell(`
    <h2 class="auth-title">${escapeHtml(title)}</h2>
    <p class="auth-subtitle">${escapeHtml(message)}</p>
    <a href="#/login" class="btn btn-primary w-full btn-lg">Back to log in</a>
  `);
}

function formView() {
  return shell(`
    <h2 class="auth-title">Set a new password</h2>
    <p class="auth-subtitle">Choose a new password for your account.</p>
    <form class="auth-form" id="reset-password-form">
      <div class="form-group">
        <label class="form-label" for="reset-password">New Password</label>
        <input type="password" class="form-input" id="reset-password"
               placeholder="At least ${MIN_PASSWORD_LENGTH} characters"
               minlength="${MIN_PASSWORD_LENGTH}" autocomplete="new-password" required>
      </div>
      <div class="form-group">
        <label class="form-label" for="reset-password-confirm">Confirm Password</label>
        <input type="password" class="form-input" id="reset-password-confirm"
               placeholder="Re-enter your new password" autocomplete="new-password" required>
      </div>
      <div class="auth-error" id="reset-password-error" hidden></div>
      <button type="submit" class="btn btn-primary w-full btn-lg" id="reset-password-submit">
        Update password
      </button>
    </form>
    <div class="auth-footer">
      Remembered it? <a href="#/login">Log in</a>
    </div>
  `);
}

/**
 * Puts the session the reset link carries in place, immediately before it is
 * used.
 *
 * The tokens in the link are the authority for *which* account is being reset,
 * so they take precedence over whatever session the browser already holds —
 * otherwise opening a link for one account while signed in as another would
 * change the wrong password. Falling back to the stored session covers the case
 * where supabase-js detected the callback before this module saw it.
 */
async function establishSession(client, cb) {
  if (cb.accessToken && cb.refreshToken) {
    const { data, error } = await client.auth.setSession({
      access_token: cb.accessToken,
      refresh_token: cb.refreshToken,
    });
    if (error) throw error;
    return data.session;
  }

  const { data } = await client.auth.getSession();
  return data?.session || null;
}

function bindForm(container) {
  const form = container.querySelector('#reset-password-form');
  const password = container.querySelector('#reset-password');
  const confirm = container.querySelector('#reset-password-confirm');
  const errorBox = container.querySelector('#reset-password-error');
  const submit = container.querySelector('#reset-password-submit');

  const fail = (message) => {
    errorBox.textContent = message;
    errorBox.hidden = false;
    submit.disabled = false;
    submit.textContent = 'Update password';
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    errorBox.hidden = true;

    if (password.value.length < MIN_PASSWORD_LENGTH) {
      fail(`Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password.value !== confirm.value) {
      fail('Passwords do not match.');
      return;
    }

    const client = legacy('supabaseClient');
    if (!client) {
      fail('Password reset is unavailable in demo mode.');
      return;
    }

    submit.disabled = true;
    submit.textContent = 'Updating…';

    try {
      const session = await establishSession(client, callback || {});
      if (!session) {
        fail('This reset link is no longer valid. Request a new one from the log in page.');
        return;
      }

      const { error } = await client.auth.updateUser({ password: password.value });
      if (error) throw error;

      toast('Password updated — welcome back. 💪', 'success', 4000);
      // The reset is done, so stop claiming the route. The reload below makes
      // this moot by rebuilding the module, but not if a beforeunload handler
      // holds the page — an active workout installs one.
      leaveRecovery();
      // A full reload rather than a route change: the legacy core loads a
      // user's data once, in `initApp`, from the session it finds at startup.
      // Reloading is what gets the app to that path with the new session, and
      // it leaves no half-initialised state behind.
      replaceRoute('/dashboard');
      window.location.reload();
    } catch (err) {
      fail(err?.message || 'Could not update your password. Request a new link and try again.');
    }
  });
}

function renderReset() {
  const container = document.getElementById('view-container');
  if (!container) return;

  // Charts hold canvases that leak if the view is replaced without disposing
  // them; the legacy router does the same teardown on every route change.
  const charts = globalThis.AppState?.chartInstances || {};
  for (const key of Object.keys(charts)) {
    charts[key]?.destroy?.();
    delete charts[key];
  }

  hideChrome();
  container.className = 'view-enter';
  window.scrollTo(0, 0);

  // Every way off this screen is one of the links it renders. They are ordinary
  // hash links, so the router would run while `recoveryActive` still held and
  // bring the user straight back; taking the flag down on the way out is what
  // makes "Back to log in" mean it.
  container.addEventListener('click', (event) => {
    if (event.target.closest('a[href^="#/"]')) leaveRecovery();
  });

  const cb = capture() || {};

  if (cb.error) {
    // GoTrue's own wording first — it distinguishes an expired link from one
    // already used — then what to do about it, which it never says.
    const lead = cb.errorDescription ? `${cb.errorDescription.replace(/\.$/, '')}. ` : '';
    container.innerHTML = deadLinkView(
      'This link no longer works',
      `${lead}Reset links can be used once, and not for long. Ask for a new one from the log in page.`,
    );
    return;
  }

  if (!legacy('supabaseClient')) {
    container.innerHTML = deadLinkView(
      'Not available in demo mode',
      'Demo mode has no account behind it, so there is no password to reset.',
    );
    return;
  }

  container.innerHTML = formView();
  bindForm(container);
}

export function installPasswordReset() {
  // Read the URL now, before supabase-js finishes initialising and strips the
  // fragment. This module runs while the document is still parsing, which is
  // the earliest any module can look.
  const landed = capture();

  wrapGlobal('handleRoute', function (next) {
    const route = (window.location.hash || '#/').slice(1) || '/';
    // `recoveryActive` first: it is the one signal that outlives the URL, so a
    // route change that lost the fragment still lands back on the form rather
    // than on the landing page or, for the session the link carries, onboarding.
    if (!recoveryActive && route !== RESET_ROUTE && !parseRecoveryCallback(window.location)) {
      return next();
    }

    replaceRoute(RESET_ROUTE);
    return renderReset();
  });

  if (!landed) return;
  recoveryActive = true;

  // The callback lands on `/`, which the legacy router would answer with the
  // landing page. Pointing the URL at the reset route here — before `initApp()`
  // routes for the first time at DOMContentLoaded — means the first render is
  // already the right one, with no landing page flashing past on the way.
  // `replaceState` rather than a navigation, so `back` does not lead to a URL
  // with the tokens still in it.
  replaceRoute(RESET_ROUTE);
}
